<template>
  <div class="max-picker" :data-open="open || undefined">
    <div v-if="multiple && selectedItems.length" class="max-picker__chips">
      <button
        v-for="item in selectedItems"
        :key="String(item.value)"
        type="button"
        class="max-picker__chip"
        :disabled="disabled"
        @click="remove(item.value)"
      >{{ item.label }} <span aria-hidden="true">×</span></button>
    </div>
    <input
      :id="inputId"
      class="max-input"
      type="search"
      :value="open || !singleLabel ? query : singleLabel"
      :placeholder="placeholder"
      :disabled="disabled"
      :aria-expanded="open"
      aria-autocomplete="list"
      :aria-controls="listId"
      autocomplete="off"
      @focus="onFocus"
      @input="onType(($event.target as HTMLInputElement).value)"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="chooseActive"
      @keydown.esc.prevent="open = false"
    >
    <ul v-if="open" :id="listId" ref="list" class="max-picker__list" role="listbox" @scroll.passive="onListScroll">
      <li v-if="!visible.length" class="max-picker__empty">{{ emptyLabel }}</li>
      <li
        v-for="(option, i) in visible"
        :key="String(option.value) + '-' + i"
        role="option"
        :aria-selected="isSelected(option.value)"
        :data-active="i === active || undefined"
        @mousedown.prevent="pick(option)"
      >{{ option.label }}</li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useMaxContext, useMaxInterface } from '../runtime/context.ts'

type Option = { label: string, value: unknown, options?: Option[] }

const props = defineProps<{
  options?: Option[]
  modelValue?: unknown
  multiple?: boolean
  placeholder?: string
  disabled?: boolean
  optionsState?: string | null
  inputId?: string
}>()

const emit = defineEmits<{ (e: 'update:modelValue', value: unknown): void }>()
const iface = useMaxInterface()
const ctx = useMaxContext()
const listId = 'max-picker-' + Math.random().toString(36).slice(2, 8)
const open = ref(false)
const query = ref('')
const active = ref(0)
const more = ref<boolean | null>(null)
const labels = ref(new Map<string, string>())
let timer: ReturnType<typeof setTimeout> | null = null
let inFlight = false
let searchGen = 0
const DELAY = 300

const busy = computed(() => iface?.busy.value === true)
const serverSearch = computed(() => !!props.optionsState && !!iface?.canSearchList(props.optionsState))
const emptyLabel = computed(() => query.value ? ctx.labels.ui.noRows : (ctx.labels.ui.typeToSearch ?? ctx.labels.ui.noRows))
const list = ref<HTMLElement | null>(null)
const SCROLL_GAP = 48

function flatten (options: Option[] | undefined): Option[] {
  const out: Option[] = []
  for (const option of options ?? []) {
    if (option?.options) out.push(...option.options)
    else if (option) out.push(option)
  }
  return out
}

function keyOf (value: unknown): string {
  return String(value)
}

function remember (option: Option) {
  labels.value.set(keyOf(option.value), option.label)
}

watch(() => props.options, options => {
  for (const option of flatten(options)) remember(option)
}, { immediate: true })

const selectedValues = computed(() => {
  if (props.multiple) return Array.isArray(props.modelValue) ? props.modelValue : []
  return props.modelValue === null || props.modelValue === undefined || props.modelValue === '' ? [] : [props.modelValue]
})

const selectedItems = computed(() => selectedValues.value.map(value => ({
  value,
  label: labels.value.get(keyOf(value)) ?? String(value)
})))

const singleLabel = computed(() => props.multiple ? '' : (selectedItems.value[0]?.label ?? ''))

const visible = computed(() => {
  const loaded = flatten(props.options)
  const extra = selectedItems.value.filter(item => !loaded.some(option => keyOf(option.value) === keyOf(item.value)))
  const all = [...extra, ...loaded]
  if (serverSearch.value) return all
  const q = query.value.trim().toLowerCase()
  return q ? all.filter(option => option.label.toLowerCase().includes(q)) : all
})

const showMore = computed(() => {
  if (!serverSearch.value) return false
  if (more.value === true) return true
  if (more.value === false) return false
  return flatten(props.options).length >= 50
})

function isSelected (value: unknown) {
  return selectedValues.value.some(v => keyOf(v) === keyOf(value))
}

function commit (values: unknown[]) {
  emit('update:modelValue', props.multiple ? values : (values[0] ?? null))
}

function pick (option: Option) {
  remember(option)
  if (props.multiple) {
    commit(isSelected(option.value)
      ? selectedValues.value.filter(v => keyOf(v) !== keyOf(option.value))
      : [...selectedValues.value, option.value])
  } else {
    commit([option.value])
    open.value = false
    query.value = ''
  }
}

function remove (value: unknown) {
  commit(selectedValues.value.filter(v => keyOf(v) !== keyOf(value)))
}

function onFocus () {
  open.value = true
  query.value = ''
  nextTick(fillIfShort)
}

function onType (value: string) {
  open.value = true
  query.value = value
  if (!serverSearch.value || !props.optionsState || !iface) return
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => search(value, 0), DELAY)
}

async function search (value: string, offset: number) {
  if (!props.optionsState || !iface) return
  if (inFlight && offset > 0) return
  const gen = ++searchGen
  inFlight = true
  try {
    const result = await iface.searchList(props.optionsState, value, offset)
    if (gen !== searchGen) return
    more.value = result.more
    if (offset === 0) queueMicrotask(fillIfShort)
  } finally {
    if (gen === searchGen) inFlight = false
  }
}

async function loadMore () {
  if (!showMore.value || inFlight) return
  await search(query.value, flatten(props.options).length)
}

function onListScroll () {
  const el = list.value
  if (!el || inFlight || !showMore.value) return
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_GAP) loadMore()
}

function fillIfShort () {
  const el = list.value
  if (!el || inFlight || !showMore.value) return
  if (el.scrollHeight <= el.clientHeight + SCROLL_GAP) loadMore()
}

function move (delta: number) {
  if (!visible.value.length) return
  open.value = true
  active.value = (active.value + delta + visible.value.length) % visible.value.length
}

function chooseActive () {
  const option = visible.value[active.value]
  if (option) pick(option)
}

watch(visible, () => { active.value = 0 })

function onDoc (event: MouseEvent) {
  const root = (event.target as HTMLElement | null)?.closest?.('.max-picker')
  if (!root) open.value = false
}

if (typeof document !== 'undefined') document.addEventListener('mousedown', onDoc)
onBeforeUnmount(() => {
  if (timer) clearTimeout(timer)
  if (typeof document !== 'undefined') document.removeEventListener('mousedown', onDoc)
})
</script>
